// -----------------------------------------------------------------------------
// storageKeys.ts — browser-storage key prefix + one-off carry-over (v0.5.1, round 22 #5: DBM Hub → BuildNexx).
//
// Settings remembered per PC live in localStorage under "buildnexx-…" keys:
//   buildnexx-a1-view / buildnexx-a1-sort (ProjectsPage), buildnexx-sidebar-collapsed (Shell),
//   buildnexx-collapsed-<card> (kit.tsx CollapsibleCard). The demo sign-in (api/dev.ts) uses sessionStorage
//   "buildnexx-dev-user".
// Up to v0.5.0 the same keys started with "dbm-". carryOverLegacyKeys() runs once at start-up (main.tsx, before the
// first render) and moves every "dbm-…" key to "buildnexx-…", so nobody loses a remembered view, sort or collapsed
// card. A key already set under the new name wins. Safe to run on every start: with no old keys it does nothing.
// Test: tests/unit/storageKeys.test.ts.
// -----------------------------------------------------------------------------
export const KEY_PREFIX = "buildnexx-";
export const LEGACY_PREFIX = "dbm-";

/** The bits of Storage this needs — lets the unit test pass a plain in-memory store. */
export type KeyStore = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;

/** Move every legacy "dbm-…" key in `store` to "buildnexx-…". Returns how many keys were looked at. */
export function carryOverKeys(store: KeyStore): number {
  const old: string[] = [];
  for (let i = 0; i < store.length; i++) { const k = store.key(i); if (k?.startsWith(LEGACY_PREFIX)) old.push(k); }
  for (const k of old) {
    const next = KEY_PREFIX + k.slice(LEGACY_PREFIX.length);
    const v = store.getItem(k);
    if (store.getItem(next) === null && v !== null) store.setItem(next, v);
    store.removeItem(k);
  }
  return old.length;
}

/** Start-up hook: both storages; private windows / blocked storage are simply skipped. */
export function carryOverLegacyKeys(): void {
  for (const get of [() => window.localStorage, () => window.sessionStorage]) {
    try { carryOverKeys(get()); } catch { /* storage unavailable — nothing to carry over */ }
  }
}
